import React, { useMemo } from "react";
import { ScrollView, StyleSheet, useWindowDimensions } from "react-native";

import {
  BundleChecklistFragment,
  NoSubscriptionOptionInBundle,
  formatPrice,
  getInsuranceByCountryCode,
  isNative,
  useBrowser,
  useListReviewsQuery,
  useV2GetCheckoutQuery,
} from "@stepapp/core";
import { ReviewSortFields, SortDirection } from "@stepapp/core/apiTypes";
import {
  Block,
  BottomSheet,
  CollapsibleChecklist,
  Divider,
  RatingScale,
  SvgIcon,
  Touchable,
  Typography,
  palette,
  spacing,
} from "@stepapp/ui";
import { useTranslation } from "react-i18next";
import { getAttributeAnswers, getServiceAnswers } from "../../helpers";
import { useAppLinks } from "../../hooks";
import { AppStorage } from "../../services/AppStorage";
import AverageCleaningTime from "../Service/AverageCleaningTime";

type Props = {
  checklist?: BundleChecklistFragment[];
  hasSelectedOptions: boolean;
  detailsShown: boolean;
  handleClose: () => void;
};

const ServiceDetailsSheet = ({
  checklist,
  hasSelectedOptions,
  handleClose,
  detailsShown,
}: Props) => {
  const { t } = useTranslation();
  const countryCode = AppStorage().get("countryCode");
  const { StepCover } = useAppLinks();
  const openStepCover = useBrowser(StepCover);
  const priceFormatter = formatPrice(countryCode);
  const insurance = getInsuranceByCountryCode(countryCode);

  const { data } = useV2GetCheckoutQuery({
    skip: !detailsShown,
    fetchPolicy: "no-cache",
  });
  const { data: reviews } = useListReviewsQuery({
    variables: {
      filter: { rating: { eq: 5 }, bundleId: { eq: data?.v2GetCheckout?.bundle?.id } },
      sorting: { direction: SortDirection.Desc, field: ReviewSortFields.ReservationStartedAt },
    },
  });

  const answers = useMemo(() => {
    const attributeAnswers = getAttributeAnswers(data?.v2GetCheckout?.attributes || []);
    const serviceAnswers = getServiceAnswers(data?.v2GetCheckout?.services || []);

    return [...attributeAnswers, ...serviceAnswers];
  }, [data?.v2GetCheckout?.attributes, data?.v2GetCheckout?.services]);

  const price = useMemo(
    () =>
      data?.v2GetCheckout?.priceAfterDiscounts
        ? priceFormatter(data?.v2GetCheckout?.priceAfterDiscounts)
        : data?.v2GetCheckout?.bundle?.startingPrice,
    [
      priceFormatter,
      data?.v2GetCheckout?.bundle?.startingPrice,
      data?.v2GetCheckout?.priceAfterDiscounts,
    ],
  );

  const { height: screenHeight } = useWindowDimensions();

  const sheetStyles = { maxHeight: screenHeight * 0.9 };

  const priceAfterDiscounts = useMemo(() => {
    if (!data?.v2GetCheckout?.orderHouseArea) return "";
    const { price, discount } = data.v2GetCheckout.orderHouseArea;

    const periodicityDiscount = discount?.reduce((acc, d) => acc + (d?.fixedValue || 0), 0) || 0;

    return priceFormatter(price - periodicityDiscount);
  }, [priceFormatter, data?.v2GetCheckout?.orderHouseArea]);

  const hasSubscriptionOption =
    data?.v2GetCheckout?.periodicity?.title !== NoSubscriptionOptionInBundle;

  return (
    <BottomSheet
      containerStyle={styles.bottomSheetContainer}
      contentStyle={[styles.container, sheetStyles]}
      enableScroll={false}
      toggle={handleClose}
      visible={detailsShown}
    >
      <Block alignStart padding={2.5}>
        <Touchable onPress={handleClose}>
          <Block alignCenter justifyCenter style={styles.closeIcon}>
            <SvgIcon color={palette.common.gray} name="cross" size={36} />
          </Block>
        </Touchable>
        <Typography variant="newH2">{t("common.order")}</Typography>
      </Block>

      <Divider spaceless color={palette.secondary.grey} />

      <ScrollView>
        {hasSelectedOptions ? (
          <Block marginTop={4} paddingHorizontal={2.5}>
            {data?.v2GetCheckout?.timeConsumption && (
              <>
                <AverageCleaningTime
                  showClockIcon
                  timeConsumption={data?.v2GetCheckout?.timeConsumption}
                  categoryType={data?.v2GetCheckout?.bundle?.categoryType}
                />

                <Divider color={palette.secondary.grey} />
              </>
            )}

            <Block>
              <Block justifySpace row paddingVertical={2}>
                <Block>
                  <Typography variant="paragraph">{data?.v2GetCheckout?.bundle?.name}</Typography>
                  {hasSubscriptionOption && (
                    <Typography type="secondary">
                      {data?.v2GetCheckout?.periodicity?.title}
                    </Typography>
                  )}
                </Block>

                <Block alignEnd>
                  <Typography variant="newH3">{priceAfterDiscounts}</Typography>

                  {!!data?.v2GetCheckout?.periodicity?.discount && hasSubscriptionOption && (
                    <Typography type="success" variant="body">
                      -{data?.v2GetCheckout?.periodicity?.discount}%
                    </Typography>
                  )}
                </Block>
              </Block>

              {answers.map(({ id, title, quantity, price, description }) => (
                <Block justifySpace row key={`${id}-${description}`} paddingVertical={2}>
                  <Block flex={1}>
                    <Typography variant="paragraph">{title}</Typography>
                    <Typography type="secondary">{description}</Typography>
                  </Block>

                  <Block>
                    <Typography variant="newH3"> </Typography>
                    <Typography variant="body">
                      {priceFormatter(price * (quantity || 1))}
                    </Typography>
                  </Block>
                </Block>
              ))}

              <Block justifySpace row marginTop={2}>
                <Typography variant="bodyBold">{t("common.together")}</Typography>
                <Typography variant="newH3">{price}</Typography>
              </Block>
            </Block>
          </Block>
        ) : (
          <Block marginTop={4} paddingHorizontal={2.5}>
            <Typography variant="paragraph">
              {t("screens.service.selectAvailableOptions")}
            </Typography>
          </Block>
        )}

        <Block
          marginTop={3}
          paddingHorizontal={2.5}
          paddingTop={3}
          style={styles.contentBackground}
        >
          {checklist && checklist?.length > 0 && (
            <>
              <Typography variant="newH3">{t("screens.service.serviceRange")}</Typography>
              <Block marginTop={1.5}>
                <CollapsibleChecklist checklist={checklist} />
              </Block>

              <Block paddingTop={4}>
                <Divider spaceless color={palette.secondary.grey} />
              </Block>
            </>
          )}
        </Block>

        <Block paddingHorizontal={2.5} paddingTop={4} style={styles.contentBackground}>
          {reviews?.reviews?.edges && reviews?.reviews?.edges?.length > 0 && (
            <Block marginBottom={4}>
              <Typography variant="newH3">{t("screens.service.reviews")}</Typography>
              <Block row marginTop={3} paddingHorizontal={2.5}>
                <Typography variant="paragraph">&ldquo;</Typography>
                <Typography variant="paragraph">
                  {reviews?.reviews?.edges[0]?.node?.comment || "REVIEW"}
                </Typography>
                <Block justifyEnd>
                  <Typography variant="paragraph">&rdquo;</Typography>
                </Block>
              </Block>

              <Block alignCenter row marginTop={1.5} paddingHorizontal={3}>
                <RatingScale
                  currentRating={reviews?.reviews?.edges[0]?.node?.rating}
                  padding={0}
                  size={16}
                  width={2.8}
                />
                <Block marginLeft={1}>
                  <Typography type="secondary" variant="lightLabel">
                    {reviews?.reviews?.edges[0]?.node?.customerName || "NAME"}
                  </Typography>
                </Block>
              </Block>
            </Block>
          )}

          <Block marginBottom={10} marginTop={1.5}>
            <Block alignCenter row>
              <Typography variant="newH3">{t("screens.service.serviceProtects")}</Typography>
              <Block marginBottom={isNative ? 1 : 0} marginLeft={1}>
                <SvgIcon name="stepcover" />
              </Block>
            </Block>

            <Block alignCenter row paddingTop={2}>
              <SvgIcon color={palette.font.error} name="insurance" size={36} />

              <Block marginLeft={1.5}>
                <Typography variant="lightLabel">
                  {t("screens.service.serviceInsurance", { amount: insurance })}
                  <Touchable onPress={openStepCover}>
                    <Block marginLeft={0.5}>
                      <Typography decoration="underline" variant="label">
                        {t("screens.service.seeDetails")}
                      </Typography>
                    </Block>
                  </Touchable>
                </Typography>
              </Block>
            </Block>
          </Block>
        </Block>
      </ScrollView>
    </BottomSheet>
  );
};

export default React.memo(ServiceDetailsSheet);

const styles = StyleSheet.create({
  closeIcon: {
    backgroundColor: palette.common.black,
    borderRadius: 20,
    marginBottom: spacing.unit * 2.5,
  },
  container: {
    paddingBottom: 0,
    padding: 0,
  },
  bottomSheetContainer: {
    justifyContent: "flex-end",
    paddingBottom: 0,
  },
  contentBackground: {
    backgroundColor: palette.common.grayBackground,
  },
});
